import type { Editor } from '@tiptap/core';
import { BubbleMenu } from '@tiptap/react/menus';
import {
    Bold,
    Code,
    Italic,
    Strikethrough,
    Unlink
} from 'lucide-react';
import { Button } from 'ui';
import { cn } from 'utils/cn';
import LinkInsertButton from './components/LinkInsertButton';
import { unlink } from './helpers';

export type BubbleMenuProps = {
    editor: Editor | null;
};

export default function EditorBubbleMenu({ editor }: BubbleMenuProps) {
    if (!editor) return null;

    const isCursorOverLink = editor.getAttributes('link').href;

    return (
        <BubbleMenu editor={editor} options={{ offset: 6, placement: 'top' }}>
            <div className="flex items-center gap-1 rounded-lg border bg-background p-1 shadow-lg">
                <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                        'text-gray-600 h-8 w-8',
                        editor.isActive('bold') && 'bg-gray-100'
                    )}
                    onClick={() => editor.chain().focus().toggleBold().run()}
                >
                    <Bold className="h-4 w-4" />
                </Button>

                <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                        'text-gray-600 h-8 w-8',
                        editor.isActive('italic') && 'bg-gray-100'
                    )}
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                >
                    <Italic className="h-4 w-4" />
                </Button>

                <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                        'text-gray-600 h-8 w-8',
                        editor.isActive('strike') && 'bg-gray-100'
                    )}
                    onClick={() => editor.chain().focus().toggleStrike().run()}
                >
                    <Strikethrough className="h-4 w-4" />
                </Button>

                <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                        'text-gray-600 h-8 w-8',
                        editor.isActive('code') && 'bg-gray-100'
                    )}
                    onClick={() => editor.chain().focus().toggleCode().run()}
                >
                    <Code className="h-4 w-4" />
                </Button>

                <div className="w-px bg-border h-6" />

                <LinkInsertButton editor={editor} />

                <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                        'text-gray-600 h-8 w-8',
                        editor.isActive('link') && 'bg-gray-100'
                    )}
                    disabled={!isCursorOverLink}
                    onClick={() => unlink(editor)}
                >
                    <Unlink className="h-4 w-4" />
                </Button>
            </div>
        </BubbleMenu>
    );
}